"use client";

import Image from "next/image";
import { Link, usePathname } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { Suspense, useEffect, /* useMemo, */ useState } from "react";
import { useSearchParams } from "next/navigation";
import { cn } from "@/lib/utils";
import { getAssetPath, getBrandConfig, withDocsSrc } from "@/lib/brand";
import { isSspChannelParams } from "@/lib/channel";
import {
  LayoutDashboard,
  type LucideIcon,
  Component,
  BookOpenText,
  Sparkles,
  Menu,
} from "lucide-react";
import { useUser } from "@/context/UserContext";
// import { getSaasList, type SaasService } from "@/lib/api/service";

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";
import { UserCenter } from "./UserCenter";
import { LayoutControls } from "./LayoutControls";
import { Button } from "./ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";

type NavChild = {
  key: string;
  href: string;
  icon: LucideIcon;
  external?: boolean;
  // i18n key 版（静态项）与字面量版（动态服务项）二选一
  titleKey?: string;
  descKey?: string;
  title?: string;
  description?: string;
};

// 服务 slug → 图标映射（与 ServiceGrid 保持一致，兜底 Sparkles）
// const SERVICE_ICON_MAP: Record<string, LucideIcon> = {
//   brain: Sparkles,
//   shield: HandPlatter,
//   search: Search,
// };

type NavGroup = {
  key: string;
  introTitleKey?: string;
  introDescKey?: string;
  href?: string;
  external?: boolean;
  icon: LucideIcon;
  accent: string;
  items?: NavChild[];
};

function HeaderContent() {
  const t = useTranslations("nav");
  const { theme } = useTheme();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [scrolled, setScrolled] = useState(false);
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useUser();
  const brand = getBrandConfig();
  const isAuthenticated = Boolean(user);
  // 360ssp 渠道隐藏整条顶部导航
  const isSspChannel = isSspChannelParams(searchParams);
  // const [services, setServices] = useState<SaasService[]>([]);

  // useEffect(() => {
  //   getSaasList()
  //     .then(setServices)
  //     .catch(() => setServices([]));
  // }, []);

  // promoted 服务：按 sort 升序，最多 3 个
  // const promotedServices = useMemo(
  //   () =>
  //     services
  //       .filter((s) => s.promoted)
  //       .sort((a, b) => a.sort - b.sort)
  //       .slice(0, 3),
  //   [services],
  // );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // 获取实际的主题颜色（处理跟随系统的情况）
  useEffect(() => {
    const getResolvedTheme = () => {
      if (theme === "system") {
        const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
          .matches
          ? "dark"
          : "light";
        setResolvedTheme(systemTheme);
      } else {
        setResolvedTheme(theme as "light" | "dark");
      }
    };

    getResolvedTheme();

    // 监听系统主题变化
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (theme === "system") {
        setResolvedTheme(mediaQuery.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  const logoSrc =
    resolvedTheme === "dark"
      ? getAssetPath(brand.logoDark)
      : getAssetPath(brand.logoLight);

  const navGroups: NavGroup[] = [
    {
      key: "modelHub",
      href: "/models",
      icon: Component,
      accent:
        "from-indigo-600 to-blue-500/90 dark:from-indigo-700 dark:to-blue-600/80",
    },
    ...(brand.headerLinks.docs
      ? [
          {
            key: "docs",
            href: withDocsSrc(brand.headerLinks.docs, isSspChannel),
            external: true,
            icon: BookOpenText,
            accent:
              "from-violet-600 to-fuchsia-500/90 dark:from-violet-700 dark:to-fuchsia-600/80",
          },
        ]
      : []),
    ...(brand.headerLinks.playground
      ? [
          {
            key: "playground",
            href: brand.headerLinks.playground,
            external: true,
            icon: Sparkles,
            accent:
              "from-amber-500 to-orange-500/90 dark:from-amber-600 dark:to-orange-600/80",
          },
        ]
      : []),
    // {
    //   key: "saasService",
    //   introTitleKey: "saasServiceIntroTitle",
    //   introDescKey: "saasServiceIntroDesc",
    //   icon: HandPlatter,
    //   accent:
    //     "from-cyan-600 to-sky-500/90 dark:from-cyan-700 dark:to-sky-600/80",
    //   items: [
    //     {
    //       key: "saasOverviewEntry",
    //       href: "/saas",
    //       titleKey: "saasOverviewEntry",
    //       descKey: "saasOverviewEntryDesc",
    //       icon: LayoutGrid,
    //     },
    //     ...promotedServices.map((s) => ({
    //       key: s.slug,
    //       href: s.landing_url || "/saas",
    //       title: s.name,
    //       description: s.tagline,
    //       icon: SERVICE_ICON_MAP[s.icon] ?? Sparkles,
    //     })),
    //   ],
    // },
  ];

  const isPathMatch = (href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

  if (isSspChannel) {
    return null;
  }

  return (
    <header
      className={`sticky top-0 z-30 w-full transition border-b border-border ${
        scrolled
          ? "border-b border-border/60 bg-background/80 backdrop-blur-xl"
          : "bg-background/60"
      }`}
    >
      <div className="flex w-full items-center gap-2 px-3 py-2.5 sm:px-6 lg:px-6">
        {/* 左侧 Logo 和标题 */}
        <div className="min-w-0 shrink-0 md:w-[247px]">
          <Link href="/" className="flex items-center gap-3">
            <Image
              src={logoSrc}
              alt={t("brand")}
              width={36}
              height={36}
              className="size-7"
              priority
            />
            <div className="truncate text-base font-semibold sm:text-lg">
              {t("brand")}
            </div>
          </Link>
        </div>

        {/* 中间导航 - 自适应宽度，居左 */}
        <div className="max-md:hidden flex-1 md:block">
          <NavigationMenu>
            <NavigationMenuList>
              {navGroups.map((group) => {
                const hasChildren = Boolean(group.items?.length);
                const groupActive = hasChildren
                  ? group.items?.some(
                      (item) => !item.external && isPathMatch(item.href),
                    )
                  : group.href && !group.external
                    ? isPathMatch(group.href)
                    : false;
                const GroupIcon = group.icon;

                if (!hasChildren) {
                  return (
                    <NavigationMenuItem key={group.key}>
                      <NavigationMenuLink asChild>
                        {group.external ? (
                          <a
                            href={group.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              "px-4",
                              groupActive && "bg-accent text-accent-foreground",
                            )}
                          >
                            <span className="inline-flex items-center gap-2">
                              <GroupIcon className="size-4 shrink-0" />
                              <span>{t(group.key)}</span>
                            </span>
                          </a>
                        ) : (
                          <Link
                            href={group.href || "/"}
                            className={cn(
                              "px-4",
                              "gap-2",
                              groupActive && "bg-accent text-accent-foreground",
                            )}
                          >
                            <span className="inline-flex items-center gap-2">
                              <GroupIcon className="size-4 shrink-0" />
                              <span>{t(group.key)}</span>
                            </span>
                          </Link>
                        )}
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  );
                }

                return (
                  <NavigationMenuItem key={group.key}>
                    <NavigationMenuTrigger
                      className={cn(
                        "bg-transparent hover:bg-accent/80 focus:bg-accent/80 data-[state=open]:bg-accent/80",
                        groupActive && "bg-accent text-accent-foreground",
                      )}
                    >
                      <span className="inline-flex items-center gap-2">
                        <GroupIcon className="size-4 shrink-0" />
                        <span>{t(group.key)}</span>
                      </span>
                    </NavigationMenuTrigger>
                    <NavigationMenuContent className="p-0">
                      <div className="grid w-[500px] gap-0 md:grid-cols-1 lg:w-[600px]">
                        <div
                          className={cn(
                            "p-6 rounded-t-sm bg-gradient-to-r",
                            group.accent,
                          )}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center shadow-sm">
                              <GroupIcon className="h-6 w-6 text-white" />
                            </div>
                            <div>
                              <div className="text-lg font-medium text-white">
                                {group.introTitleKey
                                  ? t(group.introTitleKey)
                                  : t(group.key)}
                              </div>
                              <div className="text-sm text-white/90">
                                {group.introDescKey
                                  ? t(group.introDescKey)
                                  : ""}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-card p-2 rounded-b-lg shadow-sm">
                          <ul className="grid gap-3 md:grid-cols-1">
                            {group.items?.map((item) => (
                              <NavListItem
                                key={`${group.key}-${item.key}`}
                                title={
                                  item.title ?? t(item.titleKey ?? item.key)
                                }
                                description={
                                  item.description ??
                                  (item.descKey ? t(item.descKey) : "")
                                }
                                href={item.href}
                                external={item.external}
                                icon={item.icon}
                                active={
                                  !item.external && isPathMatch(item.href)
                                }
                              />
                            ))}
                          </ul>
                        </div>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                );
              })}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* 右侧用户中心 */}
        <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-3">
          {isAuthenticated && (
            <Link
              href={`/platform/account`}
              className="max-sm:hidden items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition hover:bg-accent hover:text-accent-foreground sm:flex"
            >
              <LayoutDashboard className="h-4 w-4" />
              <span>{t("consoleHeader")}</span>
            </Link>
          )}
          <LayoutControls />
          <UserCenter />
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="size-10 rounded-full md:hidden"
                aria-label="打开导航"
              >
                <Menu className="size-4" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[calc(100vw-2rem)] max-w-sm"
            >
              <SheetHeader>
                <SheetTitle>{t("brand")}</SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-2">
                {isAuthenticated && (
                  <Link
                    href="/platform/account"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium text-foreground hover:bg-accent"
                  >
                    <LayoutDashboard className="size-4 shrink-0 text-muted-foreground" />
                    <span>{t("consoleHeader")}</span>
                  </Link>
                )}
                {navGroups.map((group) => {
                  const GroupIcon = group.icon;
                  if (group.items?.length) {
                    return (
                      <div key={group.key} className="space-y-1">
                        <div className="flex min-h-10 items-center gap-3 px-3 text-sm font-medium text-muted-foreground">
                          <GroupIcon className="size-4 shrink-0" />
                          <span>{t(group.key)}</span>
                        </div>
                        <div className="space-y-1 pl-4">
                          {group.items.map((item) => {
                            const ItemIcon = item.icon;
                            const itemClassName =
                              "flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm text-foreground hover:bg-accent";
                            return item.external ? (
                              <a
                                key={`${group.key}-${item.key}`}
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={itemClassName}
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                <ItemIcon className="size-4 shrink-0 text-muted-foreground" />
                                <span>{t(item.key)}</span>
                              </a>
                            ) : (
                              <Link
                                key={`${group.key}-${item.key}`}
                                href={item.href}
                                className={itemClassName}
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                <ItemIcon className="size-4 shrink-0 text-muted-foreground" />
                                <span>{t(item.key)}</span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  const linkClassName =
                    "flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm font-medium text-foreground hover:bg-accent";
                  return group.external ? (
                    <a
                      key={group.key}
                      href={group.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClassName}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <GroupIcon className="size-4 shrink-0 text-muted-foreground" />
                      <span>{t(group.key)}</span>
                    </a>
                  ) : (
                    <Link
                      key={group.key}
                      href={group.href || "/"}
                      className={linkClassName}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <GroupIcon className="size-4 shrink-0 text-muted-foreground" />
                      <span>{t(group.key)}</span>
                    </Link>
                  );
                })}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

export function Header() {
  return (
    <Suspense fallback={null}>
      <HeaderContent />
    </Suspense>
  );
}

function NavListItem({
  title,
  description,
  href,
  icon: Icon,
  external,
  active = false,
}: {
  title: string;
  description: string;
  href: string;
  icon: LucideIcon;
  external?: boolean;
  active?: boolean;
}) {
  const itemClassName = cn(
    "rounded-md px-3 py-3 transition-colors hover:bg-accent/70 focus:bg-accent/70",
    active && "bg-accent text-accent-foreground",
  );

  return (
    <li>
      <NavigationMenuLink asChild>
        {external ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={itemClassName}
          >
            <div className="flex items-start gap-3">
              {/* <div className="mt-0.5 rounded-md bg-primary/10 p-2">
                <Icon className="size-4 text-primary" />
              </div> */}
              <div>
                <div className="text-[15px] font-semibold leading-5 text-foreground">
                  {title}
                </div>
                <p
                  className={cn(
                    "mt-1 text-[13px] leading-5 text-muted-foreground",
                    active && "text-accent-foreground/80",
                  )}
                >
                  {description}
                </p>
              </div>
            </div>
          </a>
        ) : (
          <Link href={href} className={itemClassName}>
            <div className="flex items-start gap-3">
              {/* <div className="mt-0.5 rounded-md bg-primary/10 p-2">
                <Icon className="size-4 text-primary" />
              </div> */}
              <div>
                <div className="text-[15px] font-semibold leading-5 text-foreground">
                  {title}
                </div>
                <p
                  className={cn(
                    "mt-1 text-[13px] leading-5 text-muted-foreground",
                    active && "text-accent-foreground/80",
                  )}
                >
                  {description}
                </p>
              </div>
            </div>
          </Link>
        )}
      </NavigationMenuLink>
    </li>
  );
}
